<template>
  <div class="container">
    <el-card>
      <h3 class="text-center">Parent Wallets</h3>
      <div class="header-controls">
        <el-input 
          placeholder="Search by Parent Name" 
          v-model="searchQuery" 
          @input="fetchWallets" 
          clearable 
        />
        <el-button type="primary" @click="openCreateDialog">Create Wallet +</el-button>
      </div>

      <el-table :data="wallets" style="width: 100%" v-loading="loading">
        <el-table-column prop="parent.first_name" label="Parent First Name" />
        <el-table-column prop="parent.last_name" label="Parent Last Name" />
        <el-table-column prop="balance" label="Balance (LYD)" />
        <el-table-column label="Actions" width="300" align="center">
          <template #default="scope">
            <div class="action-buttons">
              <el-button @click="openAddFundsDialog(scope.row)" type="primary" plain>
                Add Funds
              </el-button>
              <el-button @click="openHistoryDialog(scope.row)" type="info" plain>
                History
              </el-button>
              <el-popconfirm
                title="Are you sure you want to delete this wallet?"
                confirm-button-text="Yes"
                cancel-button-text="No"
                @confirm="deleteWallet(scope.row.id)"
              >
                <template #reference>
                  <el-button type="danger">Delete</el-button>
                </template>
              </el-popconfirm>
            </div>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        </div>

      <el-dialog title="Create Wallet" v-model="isCreateDialogVisible">
        <el-form :model="newWallet" ref="createFormRef">
          <el-form-item label="Parent" prop="parent_id" :error="formErrors.parent_id ? formErrors.parent_id[0] : ''">
            <el-select filterable v-model="newWallet.parent_id" placeholder="Select Parent" style="width: 100%;">
              <el-option v-for="parent in parents" :key="parent.id" :label="`${parent.first_name} ${parent.last_name}`" :value="parent.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="Initial Balance" prop="balance" :error="formErrors.balance ? formErrors.balance[0] : ''">
            <el-input v-model.number="newWallet.balance" type="number" placeholder="Initial Balance" />
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="isCreateDialogVisible = false">Cancel</el-button>
            <el-button type="primary" @click="createWallet">Create</el-button>
          </span>
        </template>
      </el-dialog>

      <el-dialog title="Add Funds to Wallet" v-model="isAddFundsDialogVisible">
        <el-form :model="addFundsForm" v-if="currentWallet.parent" ref="addFundsFormRef">
          <p><strong>Parent:</strong> {{ currentWallet.parent.first_name }} {{ currentWallet.parent.last_name }}</p>
          <p><strong>Current Balance:</strong> {{ currentWallet.balance }} LYD</p>
          <el-form-item label="Amount to Add" prop="amount" :error="editFormErrors.amount ? editFormErrors.amount[0] : ''">
            <el-input-number v-model="addFundsForm.amount" :min="0.01" :precision="2" style="width: 100%;" />
          </el-form-item>
          <el-form-item label="Treasury" prop="treasury_id" :error="editFormErrors.treasury_id ? editFormErrors.treasury_id[0] : ''">
            <el-select v-model="addFundsForm.treasury_id" placeholder="Select Treasury" style="width: 100%;">
              <el-option v-for="treasury in treasuries" :key="treasury.id" :label="treasury.name" :value="treasury.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="Payment Method" prop="payment_method" :error="editFormErrors.payment_method ? editFormErrors.payment_method[0] : ''">
            <el-select v-model="addFundsForm.payment_method" placeholder="Select Payment Method" style="width: 100%;">
              <el-option label="Cash" value="cash" />
              <el-option label="Card" value="card" />
              <el-option label="Bank Transfer" value="transfer" />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="isAddFundsDialogVisible = false">Cancel</el-button>
            <el-button type="primary" @click="addFundsToWallet" :loading="isSaving">Confirm & Send Notification</el-button>
          </span>
        </template>
      </el-dialog>

      <el-dialog title="Wallet Transaction History" v-model="isHistoryDialogVisible" width="60%">
        <div v-if="currentWallet.parent">
          <h4>Parent: {{ currentWallet.parent.first_name }} {{ currentWallet.parent.last_name }}</h4>
        </div>
        <el-table :data="currentTransactions" v-loading="isHistoryLoading" border>
    <el-table-column prop="created_at" label="Date" width="200">
        <template #default="{ row }">{{ new Date(row.created_at).toLocaleString() }}</template>
    </el-table-column>
    <el-table-column prop="type" label="Type" width="120">
        <template #default="{ row }">
            <el-tag :type="row.type === 'income' || row.type === 'deposit' ? 'success' : 'warning'">{{ row.type }}</el-tag>
        </template>
    </el-table-column>
    <el-table-column prop="amount" label="Amount" width="120" />
    <el-table-column label="Action" width="150" align="center">
        <template #default="{ row }">
            <el-button v-if="row.type === 'income' || row.type === 'deposit'" size="small" type="primary" @click="printReceipt(row)">
                Print Receipt
            </el-button>
        </template>
    </el-table-column>
</el-table>
        <template #footer>
          <el-button @click="isHistoryDialogVisible = false">Close</el-button>
        </template>
      </el-dialog>

    </el-card>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import debounce from 'lodash/debounce';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export default {
  components: { ElPopconfirm },
  data() {
    return {
      wallets: [],
      parents: [],
      loading: false,
      isSaving: false,
      newWallet: {
        parent_id: null,
        balance: 0,
      },
      currentWallet: {},
      formErrors: {},
      editFormErrors: {},
      isCreateDialogVisible: false,
      isAddFundsDialogVisible: false,
      searchQuery: '',
      treasuries: [],
      addFundsForm: {
        amount: 0,
        treasury_id: null,
        payment_method: 'cash',
      },
      isHistoryDialogVisible: false,
      isHistoryLoading: false,
      currentTransactions: [],
    };
  },
  methods: {
    openCreateDialog() {
      this.fetchParents();
      this.newWallet = { parent_id: null, balance: 0 };
      this.formErrors = {};
      this.isCreateDialogVisible = true;
    },
    openAddFundsDialog(wallet) {
      this.editFormErrors = {};
      this.currentWallet = { ...wallet };
      this.addFundsForm = { amount: 0, treasury_id: null, payment_method: 'cash' };
      this.fetchTreasuries();
      this.isAddFundsDialogVisible = true;
    },
    async fetchParents() {
      try {
        const response = await api.get('/parents-without-wallets');
        this.parents = response.data;
      } catch (error) {
        console.error("Error fetching parents:", error);
      }
    },
    fetchWallets: debounce(async function() {
      this.loading = true;
      try {
        const response = await api.get('/parent-wallets', {
          params: { search: this.searchQuery },
        });
        this.wallets = response.data.data || response.data;
      } catch (error) {
        console.error("Error fetching wallets:", error);
      } finally {
        this.loading = false;
      }
    }, 300),
    async createWallet() {
      this.formErrors = {};
      try {
        await api.post('/parent-wallets', this.newWallet);
        ElMessage.success('Wallet created successfully!');
        this.fetchWallets();
        this.isCreateDialogVisible = false;
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.formErrors = error.response.data.errors;
        } else {
          ElMessage.error('Error creating wallet.');
        }
      }
    },
    async fetchTreasuries() {
      try {
        const response = await api.get('/treasuries');
        this.treasuries = response.data.data || response.data;
      } catch (e) {
        ElMessage.error('Failed to load treasuries.');
      }
    },
    async addFundsToWallet() {
      if (!this.addFundsForm.amount || this.addFundsForm.amount <= 0) {
        ElMessage.warning('Please enter an amount greater than zero.');
        return;
      }
      if (!this.addFundsForm.treasury_id) {
        ElMessage.warning('Please select a treasury.');
        return;
      }
      this.isSaving = true;
      this.editFormErrors = {};
      try {
        await api.post(`/parent-wallets/${this.currentWallet.id}/add-funds`, this.addFundsForm);
        ElMessage.success('Funds added and notification sent successfully!');
        this.fetchWallets();
        this.isAddFundsDialogVisible = false;
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.editFormErrors = error.response.data.errors;
        } else {
          ElMessage.error('Failed to add funds.');
        }
      } finally {
        this.isSaving = false;
      }
    },
    async deleteWallet(id) {
      try {
        await api.delete(`/parent-wallets/${id}`);
        ElMessage.success('Wallet deleted successfully.');
        this.fetchWallets();
      } catch (error) {
        ElMessage.error('Error deleting wallet.');
      }
    },
    async openHistoryDialog(wallet) {
      this.currentWallet = wallet;
      this.isHistoryDialogVisible = true;
      this.isHistoryLoading = true;
      try {
        const response = await api.get(`/parent-wallets/${wallet.id}/transactions`);
        this.currentTransactions = response.data;
      } catch (error) {
        ElMessage.error('Failed to load transaction history.');
      } finally {
        this.isHistoryLoading = false;
      }
    },
    printReceipt(transaction) {
  const logoUrl = `${window.location.origin}/logo-school-one.png`;
  // جلب اسم ولي الأمر من المحفظة الحالية
  const parentName = `${this.currentWallet.parent.first_name} ${this.currentWallet.parent.last_name}`;
  // ✨ جلب الرصيد الحالي للمحفظة مباشرة ✨
  const currentBalance = this.currentWallet.balance; 
  
  const printContent = `
    <html>
      <head>
        <title>Receipt #${transaction.id}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap');
          body { font-family: 'Tajawal', sans-serif; direction: rtl; margin: 0; padding: 0; }
          .receipt-container { max-width: 350px; margin: 20px auto; padding: 20px; border: 1px solid #ccc; border-radius: 10px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
          .header { text-align: center; border-bottom: 2px dashed #ccc; padding-bottom: 15px; margin-bottom: 20px; }
          .header img { max-width: 100px; margin-bottom: 10px; }
          .header h2 { margin: 0; font-size: 1.2em; }
          .details-table { width: 100%; border-collapse: collapse; }
          .details-table td { padding: 8px 4px; border-bottom: 1px solid #eee; text-align: right; }
          .details-table td:last-child { text-align: left; font-weight: 500;}
          .details-table td:first-child { font-weight: bold; color: #555; }
          .total-row td { font-size: 1.1em; font-weight: bold; }
          .footer { text-align: center; margin-top: 30px; font-size: 0.9em; color: #777; }
          @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .receipt-container { box-shadow: none; border: none; margin: 0; max-width: 100%; }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <img src="${logoUrl}" alt="School Logo">
            <h2>إيصال إيداع رصيد</h2>
            <span>Wallet Deposit Receipt</span>
          </div>
          <table class="details-table">
            <tr><td>إلى:</td><td>${parentName}</td></tr>
            <tr><td>رقم الإيصال:</td><td>TXN-${transaction.id}</td></tr>
            <tr><td>التاريخ:</td><td>${new Date(transaction.created_at).toLocaleString('ar-LY')}</td></tr>
            <tr><td>طريقة الدفع:</td><td>${transaction.payment_method}</td></tr>
            <tr class="total-row"><td>المبلغ المودع:</td><td>${parseFloat(transaction.amount).toFixed(2)} د.ل</td></tr>
            <tr><td>الرصيد الحالي:</td><td>${parseFloat(currentBalance).toFixed(2)} د.ل</td></tr>
          </table>
          <div class="footer">
            شكراً لتعاونكم
          </div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}
  },
  mounted() {
    this.fetchWallets();
  },
};
</script>

<style scoped>
.container {
  margin: 20px;
}
.header-controls {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.header-controls .el-input {
  width: 300px;
}
.action-buttons {
    display: flex;
    gap: 10px;
}
.error-message {
  color: red;
  font-size: 12px;
}
.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: center;
}
</style>